'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── DRAGGABLE SECTION MANAGER ────────────────────────────────────────────────
// Shows all 26 letter sections with PDF anchors, status badges, drag-to-reorder.
// Reordered sequences are persisted to Supabase and visible to all caretakers.

interface SectionEntry {
  id: string;
  title: string;
  pdfPages: string;
  status: 'canonical' | 'platform_addition' | 'review';
  anchor: string;
  component: string;
}

const DEFAULT_SECTIONS: SectionEntry[] = [
  { id: 'opening', title: 'The Recognition', pdfPages: '1–2', status: 'canonical', anchor: '#section-opening', component: 'LetterBody' },
  { id: 'caretaker', title: 'Caretaker — Khalifah', pdfPages: '2–3', status: 'canonical', anchor: '#section-caretaker', component: 'LetterBody' },
  { id: 'the-mural', title: 'The Mural', pdfPages: '3–4', status: 'canonical', anchor: '#section-the-mural', component: 'LetterBody' },
  { id: 'roots-marifah', title: "Roots — Ma'rifah", pdfPages: '5–9', status: 'canonical', anchor: '#section-roots-marifah', component: 'LetterBody' },
  { id: 'al-qadir', title: 'Al-Qadir — The All-Powerful', pdfPages: '9–12', status: 'canonical', anchor: '#section-al-qadir', component: 'Plot4Content' },
  { id: 'aql', title: 'Aql — Intellect', pdfPages: '13–14', status: 'canonical', anchor: '#section-aql', component: 'Plot4Content' },
  { id: 'ilm', title: 'Ilm — Knowledge', pdfPages: '14–15', status: 'canonical', anchor: '#section-ilm', component: 'Plot4Content' },
  { id: 'hawas', title: 'Hawas — Senses', pdfPages: '15–17', status: 'canonical', anchor: '#section-hawas', component: 'Plot4Content' },
  { id: 'al-alim-al-hakim', title: 'Al-Alim · Al-Hakim', pdfPages: '17–18', status: 'canonical', anchor: '#section-al-alim-al-hakim', component: 'Plot4Content' },
  { id: 'entering-haal', title: '[Entering the Haal Platform]', pdfPages: '18–21', status: 'canonical', anchor: '#section-entering-haal', component: 'Plot4Content' },
  { id: 'islah', title: 'Islah — The Complete Fix', pdfPages: '21–22', status: 'canonical', anchor: '#section-islah', component: 'Plot4Content' },
  { id: 'ar-rahman-compassion', title: 'Ar-Rahman — Compassion', pdfPages: '22–23', status: 'canonical', anchor: '#section-ar-rahman-compassion', component: 'Plot4ArRahmanJibril' },
  { id: 'hadith-jibril', title: 'Hadith of Jibril', pdfPages: '24–25', status: 'canonical', anchor: '#section-hadith-jibril', component: 'Plot4ArRahmanJibril' },
  { id: 'al-haseeb-ar-raqib', title: 'Al-Haseeb · Ar-Raqib — Poem', pdfPages: '26–27', status: 'canonical', anchor: '#section-al-haseeb-ar-raqib', component: 'Plot4MissingContent' },
  { id: 'till-rows', title: 'Till Rows', pdfPages: '26–27', status: 'canonical', anchor: '#section-till-rows', component: 'Plot4MissingContent' },
  { id: 'rope-of-language', title: 'Rope of Language', pdfPages: '28–30', status: 'canonical', anchor: '#section-rope-of-language', component: 'Plot4MissingContent' },
  { id: 'amanah-trust', title: 'Amanah — Trust', pdfPages: '31–32', status: 'canonical', anchor: '#section-amanah-trust', component: 'Plot4MissingContent' },
  { id: 'haqq', title: 'Haqq — Truth · Right · Reality', pdfPages: '32–34', status: 'canonical', anchor: '#section-haqq', component: 'Plot4MissingContent' },
  { id: 'al-khaliq', title: 'Al-Khaliq — The Creator', pdfPages: '39–41', status: 'canonical', anchor: '#section-al-khaliq', component: 'Plot4MissingContent' },
  { id: 'al-khabir-al-zahir', title: 'Al-Khabir · Al-Zahir', pdfPages: '41–43', status: 'canonical', anchor: '#section-al-khabir-al-zahir', component: 'Plot4AlKhabirZahir' },
  { id: 'yaqeen-framework', title: 'Yaqeen Framework — Cherry Blossom', pdfPages: '3–4 ⚠', status: 'review', anchor: '#section-yaqeen-framework', component: 'Plot4YaqeenContent' },
  { id: 'plot4-enhancements', title: 'Two-Eye · Three-Mode · Fitra Diagnostic', pdfPages: 'Platform', status: 'platform_addition', anchor: '#section-plot4-enhancements', component: 'Plot4Enhancements' },
  { id: 'vocational-network', title: 'Vocational Network', pdfPages: 'Platform', status: 'platform_addition', anchor: '#section-vocational-network', component: 'VocationalNetwork' },
  { id: 'synthesis-panel', title: 'Synthesis Panel + Isnad', pdfPages: 'Platform', status: 'platform_addition', anchor: '#section-synthesis-panel', component: 'SynthesisPanelIsnad' },
  { id: 'daily-quest', title: 'Daily Quest Generator', pdfPages: 'Platform', status: 'platform_addition', anchor: '#section-daily-quest', component: 'DailyQuestGenerator' },
  { id: 'plot5', title: 'Root Modules · Vocation Lens', pdfPages: 'Platform', status: 'platform_addition', anchor: '#section-plot5', component: 'Plot5Enhancements' },
];

const STATUS_CONFIG = {
  canonical: { color: 'rgba(45,122,71,0.8)', label: '✓ PDF', bg: 'rgba(45,122,71,0.08)' },
  review: { color: 'rgba(201,168,76,0.9)', label: '⚠ Review', bg: 'rgba(201,168,76,0.08)' },
  platform_addition: { color: 'rgba(138,138,184,0.8)', label: '◈ Platform', bg: 'rgba(138,138,184,0.06)' },
};

export default function DraggableSectionManager() {
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState<SectionEntry[]>(DEFAULT_SECTIONS);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [communityOrder, setCommunityOrder] = useState<Record<string, number>>({});
  const [viewMode, setViewMode] = useState<'personal' | 'community'>('community');
  const dragNodeRef = useRef<number | null>(null);

  // Load community order from Supabase
  useEffect(() => {
    if (!open) return;
    const supabase = createClient();
    supabase
      .from('community_section_order')
      .select('section_id, position')
      .order('position')
      .then(({ data }) => {
        if (data && data.length > 0) {
          const orderMap: Record<string, number> = {};
          data.forEach((row: { section_id: string; position: number }) => {
            orderMap[row.section_id] = row.position;
          });
          setCommunityOrder(orderMap);
          // Apply community order to sections
          const ordered = [...DEFAULT_SECTIONS].sort((a, b) => {
            const posA = orderMap[a.id] ?? 999;
            const posB = orderMap[b.id] ?? 999;
            return posA - posB;
          });
          setSections(ordered);
        }
      });
  }, [open]);

  // Subscribe to real-time community order changes
  useEffect(() => {
    if (!open) return;
    const supabase = createClient();
    const channel = supabase
      .channel('community_section_order_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'community_section_order' }, () => {
        supabase
          .from('community_section_order')
          .select('section_id, position')
          .order('position')
          .then(({ data }) => {
            if (data) {
              const orderMap: Record<string, number> = {};
              data.forEach((row: { section_id: string; position: number }) => {
                orderMap[row.section_id] = row.position;
              });
              setCommunityOrder(orderMap);
              if (viewMode === 'community') {
                const ordered = [...DEFAULT_SECTIONS].sort((a, b) => {
                  const posA = orderMap[a.id] ?? 999;
                  const posB = orderMap[b.id] ?? 999;
                  return posA - posB;
                });
                setSections(ordered);
              }
            }
          });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [open, viewMode]);

  const handleDragStart = useCallback((index: number) => {
    dragNodeRef.current = index;
    setDragIndex(index);
  }, []);

  const handleDragEnter = useCallback((index: number) => {
    setDragOverIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragNodeRef.current !== null && dragOverIndex !== null && dragNodeRef.current !== dragOverIndex) {
      const newSections = [...sections];
      const [removed] = newSections.splice(dragNodeRef.current, 1);
      newSections.splice(dragOverIndex, 0, removed);
      setSections(newSections);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  }, [sections, dragOverIndex]);

  const saveToSupabase = useCallback(async () => {
    setSaving(true);
    const supabase = createClient();
    const updates = sections.map((s, i) => ({
      section_id: s.id,
      position: i + 1,
      status: s.status,
      pdf_pages: s.pdfPages,
      updated_at: new Date().toISOString(),
    }));
    for (const update of updates) {
      await supabase
        .from('community_section_order')
        .upsert(update, { onConflict: 'section_id' });
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }, [sections]);

  const resetToDefault = useCallback(() => {
    setSections(DEFAULT_SECTIONS);
  }, []);

  const reviewCount = sections.filter(s => s.status === 'review').length;

  return (
    <div style={{ background: 'rgba(6,13,18,0.98)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '40px 0' }}>
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        {/* Header toggle */}
        <button
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8,
            padding: '14px 20px', cursor: 'pointer', color: 'rgba(212,197,169,0.7)',
          }}
          aria-expanded={open}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.7)' }}>
              ⠿ Section Manager
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.35)' }}>
              {sections.length} sections · drag to reorder · visible to all caretakers
            </span>
            {reviewCount > 0 && (
              <span style={{ fontFamily: 'monospace', fontSize: 9, background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 4, padding: '2px 8px', color: '#c9a84c' }}>
                {reviewCount} to review
              </span>
            )}
          </div>
          <span style={{ color: 'rgba(201,168,76,0.5)', fontSize: 11, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
        </button>

        {open && (
          <div style={{ marginTop: 16, animation: 'smFadeIn 0.22s ease' }}>
            <style>{`
              @keyframes smFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
              .sm-row { transition: background 0.12s, transform 0.12s; }
              .sm-row:hover { background: rgba(255,255,255,0.025) !important; }
              .sm-row.dragging { opacity: 0.45; transform: scale(0.98); }
              .sm-row.drag-over { border-top: 2px solid rgba(201,168,76,0.5) !important; }
              .sm-drag-handle { cursor: grab; }
              .sm-drag-handle:active { cursor: grabbing; }
            `}</style>

            {/* Controls bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['community', 'personal'] as const).map(mode => (
                  <button key={mode} onClick={() => setViewMode(mode)} style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                    padding: '5px 12px', border: `1px solid ${viewMode === mode ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`,
                    background: viewMode === mode ? 'rgba(201,168,76,0.08)' : 'transparent',
                    color: viewMode === mode ? '#c9a84c' : 'rgba(212,197,169,0.4)', borderRadius: 4, cursor: 'pointer',
                  }}>
                    {mode === 'community' ? '⬡ Community Order' : '◈ My Order'}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={resetToDefault} style={{
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  padding: '5px 12px', border: '1px solid rgba(255,255,255,0.08)', background: 'transparent',
                  color: 'rgba(212,197,169,0.35)', borderRadius: 4, cursor: 'pointer',
                }}>
                  ↺ Reset
                </button>
                <button onClick={saveToSupabase} disabled={saving} style={{
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  padding: '5px 14px', border: `1px solid ${saved ? 'rgba(45,122,71,0.5)' : 'rgba(201,168,76,0.3)'}`,
                  background: saved ? 'rgba(45,122,71,0.1)' : 'rgba(201,168,76,0.06)',
                  color: saved ? 'rgba(45,200,100,0.8)' : '#c9a84c', borderRadius: 4, cursor: saving ? 'wait' : 'pointer',
                }}>
                  {saving ? '…saving' : saved ? '✓ Saved' : '↑ Publish Order'}
                </button>
              </div>
            </div>

            <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.35)', lineHeight: 1.6, marginBottom: 14 }}>
              Drag rows to reorder. Published sequences are visible to all caretakers in real time. PDF page anchors link directly to each section.
            </p>

            {/* Section rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {sections.map((section, idx) => {
                const cfg = STATUS_CONFIG[section.status];
                const isDragging = dragIndex === idx;
                const isDragOver = dragOverIndex === idx && dragIndex !== idx;
                return (
                  <div
                    key={section.id}
                    className={`sm-row${isDragging ? ' dragging' : ''}${isDragOver ? ' drag-over' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragEnter={() => handleDragEnter(idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={e => e.preventDefault()}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      background: isDragOver ? 'rgba(201,168,76,0.04)' : 'rgba(255,255,255,0.015)',
                      border: `1px solid ${isDragOver ? 'rgba(201,168,76,0.3)' : 'rgba(255,255,255,0.04)'}`,
                      borderRadius: 6, padding: '9px 12px', userSelect: 'none',
                    }}
                  >
                    {/* Drag handle */}
                    <span className="sm-drag-handle" style={{ color: 'rgba(212,197,169,0.2)', fontSize: 12, flexShrink: 0 }}>⠿</span>

                    {/* Position */}
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.2)', width: 18, textAlign: 'right', flexShrink: 0 }}>
                      {idx + 1}
                    </span>

                    {/* Status badge */}
                    <span style={{
                      fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em',
                      padding: '2px 7px', borderRadius: 3, flexShrink: 0,
                      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`,
                      whiteSpace: 'nowrap',
                    }}>
                      {cfg.label}
                    </span>

                    {/* Title */}
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.8)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {section.title}
                    </span>

                    {/* PDF pages */}
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.45)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      p.{section.pdfPages}
                    </span>

                    {/* Navigate */}
                    <a
                      href={section.anchor}
                      onClick={e => e.stopPropagation()}
                      style={{
                        fontFamily: 'monospace', fontSize: 9, color: 'rgba(45,138,135,0.7)',
                        textDecoration: 'none', flexShrink: 0, padding: '2px 8px',
                        border: '1px solid rgba(45,138,135,0.2)', borderRadius: 3,
                      }}
                    >
                      ↓ Go
                    </a>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap' }}>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
                  <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)' }}>{cfg.label}</span>
                </div>
              ))}
              <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.2)', marginLeft: 'auto' }}>
                ⠿ drag to reorder
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
